import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, Lock, Play, X, Volume2, VolumeX, Loader2, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_PASSWORD = "EvolveTW";

export default function LandingPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [showVideo, setShowVideo] = useState(false);
  const [error, setError] = useState("");
  const [isMuted, setIsMuted] = useState(false);
  const [videoStatus, setVideoStatus] = useState({ exists: false, url: null, loading: true });
  const [generating, setGenerating] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    checkVideoStatus();
  }, []);

  const checkVideoStatus = async () => {
    try {
      const response = await axios.get(`${API}/landing/video/status`);
      setVideoStatus({ ...response.data, loading: false });
    } catch (error) {
      setVideoStatus({ exists: false, url: null, loading: false });
    }
  };

  const generateVideo = async () => {
    setGenerating(true);
    toast.info("Generating AI video... This may take a few minutes.");
    try {
      const response = await axios.post(`${API}/landing/video/generate`);
      if (response.data.status === "generated" || response.data.status === "exists") {
        toast.success("Video ready!");
        setVideoStatus({ exists: true, url: response.data.url, loading: false });
      }
    } catch (error) {
      toast.error("Failed to generate video. Please try again.");
      console.error("Video generation failed:", error);
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      localStorage.setItem("evolve_auth", "true");
      navigate("/admin");
    } else {
      setError("Incorrect password");
      toast.error("Incorrect password. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex flex-col">
      {/* Header */}
      <header className="w-full py-6">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-center">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg shadow-primary/30">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <span className="text-3xl font-bold text-primary" style={{ fontFamily: 'Manrope, sans-serif' }}>
              EVOLVE
            </span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-xl w-full text-center space-y-8">
          {/* Tagline */}
          <div className="space-y-4">
            <h1 
              className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Master the Art of{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-indigo-600">
                Presentation
              </span>
            </h1>
            <p className="text-lg text-slate-600 max-w-md mx-auto leading-relaxed">
              EVOLVE is your AI-powered communication coach. Practice, record, and improve your presentation skills with intelligent feedback and personalized improvement plans.
            </p>
          </div>

          {/* Password Box */}
          <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6 border border-slate-200">
            <div className="flex items-center justify-center gap-2 text-slate-500">
              <Lock className="w-5 h-5" />
              <span className="text-sm font-medium">Admin Access</span>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  type="password"
                  placeholder="Enter admin password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError("");
                  }}
                  className="text-center h-12 text-lg rounded-full border-2 focus:border-primary"
                  data-testid="admin-password-input"
                />
                {error && (
                  <p className="text-red-500 text-sm mt-2">{error}</p>
                )}
              </div>
              <Button 
                type="submit" 
                className="w-full h-12 text-lg rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-primary/30"
                data-testid="access-portal-btn"
              >
                Access Portal
              </Button>
            </form>
          </div>

          {/* Know More Button */}
          <div>
            <Button
              variant="outline"
              onClick={() => setShowVideo(true)}
              className="rounded-full px-6 py-3 border-2 hover:bg-primary/5 hover:border-primary gap-2"
              data-testid="know-more-btn"
            >
              <Play className="w-5 h-5 text-primary" />
              Know more about EVOLVE
            </Button>
          </div>

          {/* Features Grid - Subtle */}
          <div className="grid grid-cols-3 gap-4 pt-4">
            <div className="text-center p-4 rounded-xl bg-white/50">
              <p className="text-3xl font-bold text-primary">AI</p>
              <p className="text-xs text-slate-500 mt-1">Powered Feedback</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-white/50">
              <p className="text-3xl font-bold text-primary">24/7</p>
              <p className="text-xs text-slate-500 mt-1">Practice Anytime</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-white/50">
              <p className="text-3xl font-bold text-primary">100%</p>
              <p className="text-xs text-slate-500 mt-1">Personalized</p>
            </div>
          </div>
        </div>
      </main>

      {/* Video Modal */}
      {showVideo && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6">
          <div className="relative w-full max-w-3xl bg-black rounded-2xl overflow-hidden shadow-2xl">
            {/* Close Button */}
            <button
              onClick={() => {
                setShowVideo(false);
                if (videoRef.current) {
                  videoRef.current.pause();
                }
              }}
              className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            
            {/* Mute Button - only show if video exists */}
            {videoStatus.exists && (
              <button
                onClick={() => {
                  setIsMuted(!isMuted);
                  if (videoRef.current) {
                    videoRef.current.muted = !isMuted;
                  }
                }}
                className="absolute top-4 left-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              >
                {isMuted ? (
                  <VolumeX className="w-6 h-6 text-white" />
                ) : (
                  <Volume2 className="w-6 h-6 text-white" />
                )}
              </button>
            )}

            {/* Video Content */}
            <div className="aspect-video bg-gradient-to-br from-slate-900 to-slate-800">
              {videoStatus.loading ? (
                <div className="w-full h-full flex items-center justify-center">
                  <Loader2 className="w-12 h-12 text-primary animate-spin" />
                </div>
              ) : videoStatus.exists ? (
                <video
                  ref={videoRef}
                  src={`${API}/landing/video`}
                  className="w-full h-full object-cover"
                  autoPlay
                  loop
                  muted={isMuted}
                  playsInline
                  controls
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center p-8">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-indigo-600 flex items-center justify-center mb-6">
                    <TrendingUp className="w-12 h-12 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
                    Welcome to EVOLVE
                  </h2>
                  <p className="text-slate-300 text-center max-w-md mb-6">
                    Generate an AI-powered intro video showcasing EVOLVE&apos;s capabilities using Sora 2.
                  </p>
                  <div className="max-w-lg text-center space-y-4 text-slate-300 mb-6">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="p-3 bg-white/5 rounded-lg">
                        <p className="text-primary font-semibold">📹 Record Sessions</p>
                        <p className="text-xs mt-1">Practice with your presentations</p>
                      </div>
                      <div className="p-3 bg-white/5 rounded-lg">
                        <p className="text-primary font-semibold">🤖 AI Analysis</p>
                        <p className="text-xs mt-1">Get instant feedback</p>
                      </div>
                      <div className="p-3 bg-white/5 rounded-lg">
                        <p className="text-primary font-semibold">🎯 Voice Q&A</p>
                        <p className="text-xs mt-1">Practice answering questions</p>
                      </div>
                      <div className="p-3 bg-white/5 rounded-lg">
                        <p className="text-primary font-semibold">📊 Track Progress</p>
                        <p className="text-xs mt-1">See your improvement</p>
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={generateVideo}
                    disabled={generating}
                    className="bg-gradient-to-r from-primary to-indigo-600 hover:from-primary/90 hover:to-indigo-600/90 text-white px-8 py-3 rounded-full"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Generating Video...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-5 h-5 mr-2" />
                        Generate AI Video
                      </>
                    )}
                  </Button>
                  {generating && (
                    <p className="text-xs text-slate-400 mt-4">
                      This may take 2-5 minutes. Please wait...
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-6 text-center text-sm text-slate-500">
        <p>© {new Date().getFullYear()} EVOLVE. Empowering Professional Communication.</p>
      </footer>
    </div>
  );
}
